import { Button } from "@/components/ui/button";
import {
  AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Shield, CheckCircle2, AlertTriangle, HelpCircle, ExternalLink, RefreshCw,
} from "lucide-react";
import { S, SectionBadge } from "./shared";

export function PresenceAuditSection({
  company, pChecks, foundP, inaccP, missingP, unknownP,
  gbpFound, gbpSaving, confirmGbp, clearGbp, setGbpUrl, setGbpOpen,
}) {
  if (!pChecks.length) return null;
  return (
    <AccordionItem value="presence-audit" className="border border-slate-200 rounded-lg shadow-sm overflow-hidden">
      <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-slate-50 [&[data-state=open]]:bg-slate-50">
        <div className="flex items-center gap-2 flex-1">
          <Shield className="w-4 h-4 text-teal-600" />
          <span className="text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif' }}>Presence Audit</span>
          <span className="text-[10px] text-muted-foreground ml-1">{pChecks.length} directories</span>
          <div className="flex items-center gap-1.5 ml-auto mr-3">
            <SectionBadge count={foundP.length} color="bg-emerald-100 text-emerald-700" />
            <SectionBadge count={inaccP.length} color="bg-amber-100 text-amber-700" />
            {unknownP.length > 0 && <SectionBadge count={unknownP.length} color="bg-slate-100 text-slate-600" />}
            <SectionBadge count={missingP.length} color="bg-rose-100 text-rose-700" />
          </div>
        </div>
      </AccordionTrigger>
      <AccordionContent className="px-4">
        {/* GBP verification banner: verified (green) / likely auto-match (amber) / unverified (slate) */}
        {company.gbp_verified ? (
          <div className="flex items-center gap-2 mb-3 p-2.5 rounded-lg border border-emerald-200 bg-emerald-50/60" data-testid="gbp-verified-banner">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold text-emerald-800">
                {company.gbp_match === "auto_domain"
                  ? "Google Business Profile auto-detected via Google Maps"
                  : "Google Business Profile verified"}
              </p>
              {company.gbp_url && (
                <a href={company.gbp_url} target="_blank" rel="noreferrer" className="text-[10px] text-emerald-700 underline truncate inline-flex items-center gap-1" data-testid="gbp-verified-link">
                  View profile <ExternalLink className="w-2.5 h-2.5" />
                </a>
              )}
            </div>
            <Button variant="ghost" size="sm" className="h-7 text-[11px] text-slate-500 hover:text-rose-600" onClick={clearGbp} data-testid="gbp-clear-btn">Remove</Button>
          </div>
        ) : company.gbp_likely ? (
          <div className="flex items-start gap-2 mb-3 p-2.5 rounded-lg border border-amber-200 bg-amber-50/60" data-testid="gbp-likely-banner">
            <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold text-amber-800">
                Likely Google Business Profile found via Google Maps{company.gbp_verified_name ? `: ${company.gbp_verified_name}` : ""}
              </p>
              <p className="text-[10px] text-amber-700">
                {company.google_rating ? `${company.google_rating}★ · ${company.google_reviews || 0} reviews · ` : ""}Matched by name — confirm this is the right business.
                {company.gbp_url && <> <a href={company.gbp_url} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5" data-testid="gbp-likely-link">View <ExternalLink className="w-2.5 h-2.5" /></a></>}
              </p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <Button size="sm" className="h-7 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white" disabled={gbpSaving} onClick={confirmGbp} data-testid="gbp-confirm-btn">
                {gbpSaving ? <RefreshCw className="w-3 h-3 animate-spin" /> : "Confirm"}
              </Button>
              <Button variant="ghost" size="sm" className="h-7 text-[11px] text-slate-500 hover:text-rose-600" onClick={clearGbp} data-testid="gbp-notit-btn">Not it</Button>
            </div>
          </div>
        ) : gbpFound ? (
          <div className="flex items-center gap-2 mb-3 p-2.5 rounded-lg border border-emerald-200 bg-emerald-50/60" data-testid="gbp-found-banner">
            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-semibold text-emerald-800">Google Business Profile found</p>
              <p className="text-[10px] text-emerald-700">
                {company.google_rating ? `${company.google_rating}★ · ${company.google_reviews || 0} reviews` : "Detected via Google Maps / Places"}
                {company.gbp_url && <> · <a href={company.gbp_url} target="_blank" rel="noreferrer" className="underline inline-flex items-center gap-0.5" data-testid="gbp-found-link">View profile <ExternalLink className="w-2.5 h-2.5" /></a></>}
              </p>
            </div>
            <Button variant="ghost" size="sm" className="h-7 text-[11px] text-slate-500" onClick={() => { setGbpUrl(company.gbp_url || ""); setGbpOpen(true); }} data-testid="gbp-update-btn">Update</Button>
          </div>
        ) : (
          <div className="flex items-center gap-2 mb-3 p-2.5 rounded-lg border border-slate-200 bg-slate-50/60" data-testid="gbp-verify-banner">
            <HelpCircle className="w-4 h-4 text-slate-500 shrink-0" />
            <p className="text-[11px] text-slate-600 flex-1 min-w-0">
              GBP couldn't be auto-detected (common for service-area businesses). Have the Maps link? Verify it manually.
            </p>
            <Button variant="outline" size="sm" className="h-7 text-[11px]" onClick={() => { setGbpUrl(company.gbp_url || ""); setGbpOpen(true); }} data-testid="gbp-verify-btn">
              Verify GBP
            </Button>
          </div>
        )}
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-1.5">
          {pChecks.map((ch, i) => {
            const cfg = S[ch.status] || S.missing;
            const Icon = cfg.icon;
            const socialUrl = (company.social_profiles || {})[ch.icon];
            // GBP: prefer a stored/verified profile link; otherwise fall back to a
            // Google Maps search for this exact business so the card always links
            // to the live profile (mirrors how social cards link out).
            const gbpFallback = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent([company.name, company.city, company.state].filter(Boolean).join(" "))}`;
            const url = ch.directory === "Google Business Profile"
              ? (company.gbp_url || gbpFallback)
              : (socialUrl || null);
            const clickable = ch.status === "found" && url;
            const inner = (
              <>
                <Icon className={`w-3.5 h-3.5 shrink-0 ${cfg.c}`} />
                <div className="min-w-0 flex-1">
                  <p className="text-[11px] font-medium truncate flex items-center gap-1">
                    {ch.directory}
                    {clickable && <ExternalLink className="w-2.5 h-2.5 text-slate-400 shrink-0" />}
                  </p>
                  <p className="text-[9px] text-muted-foreground truncate">{ch.recommendation?.substring(0, 60)}</p>
                </div>
              </>
            );
            const cls = `flex items-center gap-2 p-2 rounded border ${cfg.b} ${cfg.bg}/30`;
            return clickable ? (
              <a key={i} href={url} target="_blank" rel="noreferrer"
                 className={`${cls} hover:shadow-sm hover:border-slate-300 transition-colors`}
                 data-testid={`presence-link-${ch.icon || i}`}>
                {inner}
              </a>
            ) : (
              <div key={i} className={cls}>{inner}</div>
            );
          })}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
